<template>
    <div class="page" id="lessons">
        <header class="pageHeader">
            <fade-image src="assets/headers/lessons.png" />
        </header>

        <div id="scroller" class="small">
            <div class="pageHeaderIntro">
                <h1>Lessons</h1>
                <img
                    class="icon"
                    src="~@/assets/icons/headers/lessons.svg"
                    style="margin-top: -10px"
                />
            </div>
            <router-link
                class="button mb10"
                :to="{ name: 'BookChooseType' }"
                v-if="$store.state.user.lesson_settings.video_lessons_enabled"
            >
                Book a lesson
                <chevron-right-icon class="icon"></chevron-right-icon>
            </router-link>
            <router-link class="button mb10" :to="{ name: 'BookLesson' }" v-else>
                Book a lesson
                <chevron-right-icon class="icon"></chevron-right-icon>
            </router-link>

            <router-link class="button mb" :to="{ name: 'AddCredits' }">
                Lesson credits
                <chevron-right-icon class="icon"></chevron-right-icon>
            </router-link>

            <h2 class="title">Next Lesson:</h2>
            <div class="nextLesson" v-if="next_lesson">
                <span class="intro">
                    <span class="pay" :class="{ credits: next_lesson.payment_type == 'credits' }">
                        {{ next_lesson.payment_type == "on_day" ? "Pay on day" : "Paid" }}
                    </span>
                </span>
                <span class="date">
                    <span class="dateTime">{{
                        $date(next_lesson.date).format("DD MMM YYYY")
                    }}</span>
                    <span class="dateTime">
                        <template v-if="next_lesson.start != next_lesson.end">
                            {{ $date("2020-12-08 " + next_lesson.start).format("HH:mm") }} -
                            {{ $date("2020-12-08 " + next_lesson.end).format("HH:mm") }}
                        </template>
                    </span>
                </span>

                <button
                    @click.prevent="changeLesson"
                    class="button half dark"
                    type="button"
                    name="button"
                >
                    Change
                </button>
                <button
                    :disabled="busy"
                    @click.prevent="cancelLesson"
                    class="button half cancel"
                    type="button"
                    name="button"
                >
                    {{ busy ? "Please wait..." : "Cancel lesson" }}
                </button>
            </div>

            <div class="nextLesson" v-else>
                <span class="date">You have no bookings</span>
            </div>

            <h2 class="title" style="margin-bottom: 20px" v-if="playerLessons.length > 0">
                Your bookings:
                <img class="calendar" src="@/assets/calendar.svg" />
            </h2>

            <ul class="bookingList" v-if="playerLessons.length > 0">
                <li v-for="lesson in playerLessons" :key="lesson.id">
                    <router-link :to="{ name: 'LessonChange', params: { id: lesson.id } }">
                        <span>{{ $date(lesson.date).format("DD MMM YYYY") }}</span>
                        <span>
                            <template v-if="lesson.start != lesson.end">
                                {{ $date("2020-12-08 " + lesson.start).format("HH:mm") }} -
                                {{ $date("2020-12-08 " + lesson.end).format("HH:mm") }}
                            </template>
                        </span>
                        <chevron-right-icon class="icon"></chevron-right-icon>
                    </router-link>
                </li>
            </ul>

            <empty-placeholder v-if="lessons.length == 0">
                TIP: Your lesson bookings and records will appear here, get started and book your
                first lesson by tapping the above button.
            </empty-placeholder>

            <h2 class="title" v-if="lessons.length > 0" style="margin-bottom: 20px">
                Lesson Records:
                <img class="calendar" src="@/assets/records-small-dark-green.svg" />
            </h2>

            <ul class="lessonList">
                <li
                    class="historyLesson"
                    v-if="lessons[0]"
                    :class="{ noRecord: !lessons[0].record }"
                >
                    <router-link :to="{ name: 'playerLesson', params: { id: lessons[0].id } }">
                        <span v-if="!lessons[0].record">
                            {{ $date(lessons[0].date).format("DD MMM YYYY") }}
                            <strong>Awaiting record update</strong>
                            <img class="recordArrow" src="@/assets/arrow-right-white-2.svg" />
                        </span>
                        <span v-else>
                            <b
                                ><span v-if="!lessons[0].title">Untitled record</span
                                ><span class="title" v-else>{{ lessons[0].title }}</span></b
                            >
                            {{ $date(lessons[0].date).format("DD MMM YYYY") }}

                            <template v-if="lessons[0].start != lessons[0].end">
                                {{ $date("2020-12-08 " + lessons[0].start).format("HH:mm") }} -
                                {{ $date("2020-12-08 " + lessons[0].end).format("HH:mm") }}
                            </template>

                            <lesson-star :lesson="lessons[0]" />
                            <img class="recordArrow" src="@/assets/arrow-right-white.svg" />
                        </span>
                    </router-link>

                    <lesson-locked-overlay
                        v-if="isLessonRecordLocked(lessons[0])"
                        :lesson="lessons[0]"
                    />
                </li>
            </ul>

            <upgrade-to-premium-button v-if="lessons.length > 0 && !isPremium" />

            <router-link :to="{ name: 'playerLessonsHistory' }" class="button dark left">
                View lesson records
                <img class="arrow" src="@/assets/arrow-right-white-2.svg" />
            </router-link>
        </div>
    </div>
</template>

<script>
import EmptyPlaceholder from "@/components/App/empty-placeholder";
import LessonStar from "@/components/Player/Lessons/lesson-star";
import LessonLockedOverlay from "@/components/Player/Lessons/lesson-locked-overlay";
import UpgradeToPremiumButton from "@/components/Player/upgrade-to-premium-button";
import FadeImage from "@/components/App/fade-image";
import { ChevronRightIcon } from "vue-feather-icons";
import lessonRecordAccess from "@/mixins/lesson-record-access";
export default {
    name: "player-lessons",
    mixins: [lessonRecordAccess],
    components: {
        EmptyPlaceholder,
        FadeImage,
        ChevronRightIcon,
        LessonStar,
        LessonLockedOverlay,
        UpgradeToPremiumButton,
    },
    computed: {
        lessons() {
            return this.$store.state.user.lessons;
        },
        next_lesson() {
            return this.$store.state.user.next_lesson;
        },
        credits() {
            return this.$store.state.user.credits;
        },
        user() {
            return this.$store.state.user;
        },
    },
    created() {
        this.getPlayerLessons();
        this.getNextLesson();
    },
    data() {
        return {
            busy: false,
            playerLessons: false,
        };
    },
    methods: {
        getNextLesson() {
            window.axios.get("/next_lesson").then((response) => {
                this.$store.state.user.next_lesson = response.data;
            });
        },
        changeLesson() {
            this.$router.push({ name: "LessonChange", params: { id: this.next_lesson.id } });
        },
        cancelLesson() {
            var msg = "";

            var TwnetyFourHrs = this.$date().add(24, "hour");
            if (
                this.next_lesson.payment_type == "credits" &&
                this.$date(this.next_lesson.date).isBetween(this.$date(), TwnetyFourHrs)
            ) {
                var credits = this.next_lesson.duration / 15;
                msg =
                    '<br><br><span style="color:#bd6262">There is less than 24 hours before your lesson starts. If you proceed the ' +
                    credits +
                    " credits used to make this booking will not be refunded.</span>";
            }

            this.$confirm(
                "Are you sure you want to cancel this lesson?" + msg,
                () => {
                    window.axios.delete("/lesson/" + this.next_lesson.id).then((response) => {
                        this.$store.state.user.next_lesson = response.data;
                        this.getPlayerLessons();
                        this.$notify({
                            text: "Lesson cancelled",
                        });
                    });
                },
                "Yes",
                "No",
            );
        },
        getPlayerLessons() {
            window.axios
                .get("/player/" + this.$store.state.user.id + "/lessons?upcoming=true")
                .then((response) => {
                    this.playerLessons = response.data;
                });
        },
    },
};
</script>

<style lang="scss" scoped>
.arrow {
    float: right;
}
.nextLesson {
    padding: 20px;
    background: $green_2;
    border-radius: 12px;
    margin-top: 10px;
    position: relative;
    margin-bottom: 30px;
    .dateTime {
        display: inline-block;
        width: 50%;
    }
    .intro {
        display: block;
        color: $green_0;

        .pay {
            position: absolute;
            top: -15px;
            right: 20px;
            background: $red_1;
            border: 0px;
            padding: 7px 13px;
            display: block;
            margin-bottom: 20px;
            border-radius: 8px;
            color: white;
            font-size: 14px;
            &.credits {
                background: #60c45c;
            }
        }
    }
    .date {
        display: block;
        background: white;
        padding: 20px;
        margin-top: 10px;
        margin-bottom: 10px;
        font-size: 16px;
        border-radius: 12px;
        color: $green_0;
    }
}
.historyLesson {
    background: #eef5f2;
    border-radius: $radius;
    margin-bottom: 10px;
    padding: 17px 20px;
    position: relative;

    a {
        text-decoration: none;
        color: $green_0;
        font-weight: 200;
    }
    &.noRecord {
        background: $green_0;
        color: white;
        a {
            color: white;
        }
        strong {
            font-weight: 400;
            display: block;
            margin-top: 10px;
        }
    }
    b {
        font-weight: 400;
        margin-bottom: 10px;
        display: block;
    }
    .recordArrow {
        position: absolute;
        right: 15px;
        bottom: 20px;
        pointer-events: none;
    }

    .star {
        position: absolute;
        right: 35px;
        bottom: 14px;
        top: auto;
    }
}
.title {
    line-height: 1.5;
}
.calendar {
    float: right;
}
.bookingList {
    margin-bottom: 20px;
    li {
        margin-bottom: 10px;
        a {
            text-decoration: none;
            display: block;
            height: 52px;
            background: #bbf8e0;
            padding: 18px 20px;
            border-radius: $radius;
            color: $green_0;
            position: relative;
            .icon {
                position: absolute;
                right: 20px;
                top: 15px;
            }
            span {
                display: inline-block;
                width: 50%;
            }
        }
    }
}
</style>
